import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { emptyIndex, getChildren, type DocumentIndex, type DocumentItem } from '@document-explorer/core';
import { DeIconComponent } from './icon.component.js';
import { DeUiIconComponent } from './ui-icon.component.js';

interface FlatNode {
  item: DocumentItem;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
}

@Component({
  selector: 'de-tree-view',
  standalone: true,
  imports: [DeIconComponent, DeUiIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="de-tree" role="tree" aria-label="Document tree">
      @for (node of rows(); track node.item.id) {
        <div
          role="treeitem"
          class="de-tree-node"
          [attr.aria-level]="node.depth + 1"
          [attr.aria-expanded]="node.hasChildren ? node.expanded : null"
          [attr.aria-selected]="selectionEnabled() ? selectedIds().has(node.item.id) : null"
          [style.--de-depth]="node.depth"
          tabindex="0"
          (click)="onClick(node)"
          (keydown)="onKeyDown($event, node)"
        >
          @if (node.hasChildren) {
            <span
              class="de-tree-twisty"
              [class.de-tree-twisty--open]="node.expanded"
              aria-hidden="true"
            >
              <de-ui-icon name="chevronRight" />
            </span>
          } @else {
            <span class="de-tree-spacer" aria-hidden="true"></span>
          }
          <de-icon [item]="node.item" />
          <span class="de-name">{{ node.item.name }}</span>
        </div>
      }
    </div>
  `,
})
export class DeTreeViewComponent {
  readonly index = input<DocumentIndex>(emptyIndex());
  readonly cwd = input<string | null>(null);
  readonly selectedIds = input<ReadonlySet<string>>(new Set<string>());
  readonly selectionEnabled = input<boolean>(false);

  readonly open = output<DocumentItem>();
  readonly toggleSelect = output<DocumentItem>();

  private readonly expanded = signal<ReadonlySet<string>>(new Set());

  constructor() {
    // Ancestors of the current folder start open, so the tree reflects where
    // the user already is rather than collapsing under them.
    effect(() => {
      const index = this.index();
      const initial = new Set<string>();
      let cursor = this.cwd();
      while (cursor) {
        initial.add(cursor);
        cursor = index.byId.get(cursor)?.parentId ?? null;
      }
      this.expanded.set(initial);
    });
  }

  // Flattening the visible portion keeps this a flat list of rows, which is
  // what `role="tree"` wants and what a virtualizer would later need.
  protected readonly rows = computed<FlatNode[]>(() => {
    const index = this.index();
    const expanded = this.expanded();
    const out: FlatNode[] = [];

    const walk = (parentId: string | null, depth: number): void => {
      for (const item of getChildren(index, parentId)) {
        const hasChildren = (index.childrenOf.get(item.id)?.length ?? 0) > 0;
        const isOpen = expanded.has(item.id);
        out.push({ item, depth, hasChildren, expanded: isOpen });
        if (hasChildren && isOpen) walk(item.id, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  });

  protected toggle(id: string): void {
    const next = new Set(this.expanded());
    if (!next.delete(id)) next.add(id);
    this.expanded.set(next);
  }

  protected onClick(node: FlatNode): void {
    if (node.item.type === 'folder') this.toggle(node.item.id);
    else this.open.emit(node.item);
  }

  protected onKeyDown(event: KeyboardEvent, node: FlatNode): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.open.emit(node.item);
    } else if (event.key === ' ') {
      event.preventDefault();
      this.toggleSelect.emit(node.item);
    } else if (event.key === 'ArrowRight' && node.hasChildren && !node.expanded) {
      event.preventDefault();
      this.toggle(node.item.id);
    } else if (event.key === 'ArrowLeft' && node.hasChildren && node.expanded) {
      event.preventDefault();
      this.toggle(node.item.id);
    }
  }
}
